import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";

/**
 * An generic abstract service that allows values to be multicasted to many Observers.
 *
 * @class BaseService<T>
 * @template T
 */
@Injectable({
  providedIn: "root",
})
export default abstract class BaseService<T> {
  /** @internal */
  private _itemsSubject = new BehaviorSubject<T[]>([]);

  /**
   * An Observable containing an array of items.
   *
   * @type {Observable<T[]>}
   */
  items$: Observable<T[]> = this._itemsSubject.asObservable();

  /** @internal */
  private _itemSubject = new BehaviorSubject<T>({} as T);

  /**
   * An Observable containing a single item.
   *
   * @type {Observable<T>}
   */
  item$: Observable<T> = this._itemSubject.asObservable();

  /** @internal */
  protected _isEmpty = true;

  /**
   * Returns the array of items saved on the service.
   *
   * @returns {T[]} an array of items.
   */
  getItems(): T[] {
    return this._itemsSubject.getValue();
  }

  /**
   * Saves an array of items on the service.
   *
   * @param {T[]} items the array of items to save.
   */
  setItems(items: T[]): void {
    this._itemsSubject.next(items);
  }

  /**
   * Returns the item saved on the service.
   *
   * @returns {T} the saved item.
   */
  getItem(): T {
    return this._itemSubject.getValue();
  }

  /**
   * Saves an item on the service.
   *
   * @param {T} item the item to save.
   */
  setItem(item: T): void {
    this._itemSubject.next(item);
  }

  /**
   * Determines whether the service is empty.
   */
  isEmpty(): boolean {
    throw new Error("Method 'isEmpty()' must be implemented.");
  }
}
